/**
 * Mappers between the emailer file-uploader's in-memory file objects and the
 * snake_case attachment shape stored in a cadence email step's `event_config`.
 *
 * The cadences backend (EmailAttachmentConfig) stores attachments by their
 * stable S3 object key (`content_id`) plus display metadata, and resolves a
 * fresh signed download URL at send time — so we persist the object key, never
 * the ephemeral signed URL. This mirrors the persisted-attachment record used
 * by the general Outreach emailer.
 *
 * Two seams need translation because the uploader hook speaks camelCase
 * (`contentId`, `contentType`, `inline`, `contentDisposition`) while the backend
 * `event_config` speaks snake_case (`content_id`, `content_type`, `is_inline`,
 * `content_disposition`).
 */

/**
 * Serialize the uploader's file objects into the backend `event_config`
 * attachment shape. Files without a `contentId` (S3 object key) are dropped,
 * since the backend requires `content_id` to resolve the attachment at send
 * time.
 *
 * @param {Array} files - file objects from useFileUploader
 * @returns {Array<{content_id: string, filename: ?string, content_type: ?string, is_inline: boolean, content_disposition: string}>}
 */
export const filesToEventConfigAttachments = (files = []) =>
  (files || [])
    .filter((f) => f && f.contentId)
    .map((f) => ({
      content_id: f.contentId,
      filename: f.name || f.file?.name || null,
      content_type: f.file?.type || f.originalAttachment?.contentType || null,
      is_inline: !!f.isInline,
      content_disposition: f.contentDisposition || (f.isInline ? 'inline' : 'attachment'),
    }));

/**
 * Map a step's stored `event_config.attachments` (snake_case) into the
 * camelCase shape the uploader hook expects for mount-time rehydration. Entries
 * without a `content_id` are dropped. The hook re-resolves a signed download URL
 * from `contentId` on mount.
 *
 * @param {Array} attachments - snake_case attachments from event_config
 * @returns {Array<{id: ?string, contentId: string, filename: string, contentType: string, inline: boolean, contentDisposition: string}>}
 */
export const eventConfigAttachmentsToUploader = (attachments = []) =>
  (attachments || [])
    .filter((a) => a && a.content_id)
    .map((a) => ({
      id: a.id || undefined,
      contentId: a.content_id,
      filename: a.filename || '',
      contentType: a.content_type || '',
      inline: !!a.is_inline,
      contentDisposition: a.content_disposition || (a.is_inline ? 'inline' : 'attachment'),
    }));
